
import React from 'react';
import { IndianRupee, ArrowDownLeft, X, Smartphone, CheckCircle } from 'lucide-react';
import { WorkerProfile } from '../types';
import { useSession } from '../context/SessionContext';


interface WorkerWalletProps {
  worker: WorkerProfile;
  onClose: () => void;
}

export const WorkerWallet: React.FC<WorkerWalletProps> = ({ worker, onClose }) => {
  // Use session context to get full job history for local logging
  // Note: For large scale, we should fetch paged 'completed jobs' from DB. 
  // For now, myJobs contains active+recent jobs.
  const { myJobs } = useSession();

  // Filter only PAID jobs and sort by date descending (assuming list is mixed)
  // We don't have exact timestamp in job, using string date comparison or ID fallback
  const earningsHistory = myJobs
    .filter(j => j.status === 'COMPLETED' && j.isPaid)
    .reverse(); // Show newest first (assuming array append order)

  return (
    <div className="fixed inset-0 z-[5000] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl flex flex-col max-h-[90vh] animate-scaleIn">

        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
          <div>
            <h2 className="text-xl font-black text-slate-900">Earnings Log</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Direct Payment History</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-600 shadow-sm transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide space-y-6">
          {/* Total Card */}
          <div className="bg-slate-900 text-white p-8 rounded-[2rem] text-center shadow-xl shadow-slate-900/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <IndianRupee className="w-32 h-32" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-300 mb-2">Total Lifetime Earnings</p>
            <h1 className="text-5xl font-black mb-1">₹{worker.totalEarned?.toLocaleString() || 0}</h1>
            <p className="text-[9px] text-slate-400 font-bold uppercase">Paid via UPI / Direct Cash</p>
          </div>

          {/* Transaction Log */}
          <div>
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-4 ml-1">Payment Records</h3>

            {earningsHistory.length === 0 ? (
              <div className="text-center py-10 text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-[10px] font-bold uppercase">No payments recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {earningsHistory.map(job => (
                  <div key={job.id} className="bg-white border border-slate-100 p-4 rounded-2xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-green-50 text-green-600 shrink-0">
                        <ArrowDownLeft className="w-5 h-5" />
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-xs font-black text-slate-900 truncate">{job.skill} Service</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px] text-slate-400 font-bold uppercase">{job.date}</span>
                          {job.paymentMethod === 'UPI' && (
                            <span className="text-[8px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase font-bold flex items-center gap-0.5">
                              UPI {worker.upiId ? `(${worker.upiId.split('@')[0]}..)` : ''}
                            </span>
                          )}
                          {job.paymentMethod === 'CASH' && <span className="text-[8px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded uppercase font-bold">CASH</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-sm text-green-600">+₹{job.workerEarnings || job.price}</p>
                      <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex items-center justify-end gap-1 mt-0.5">
                        Received <CheckCircle className="w-2 h-2" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
